import React, { useContext, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Contexts from "../../../contexts/Contexts";
import { useLogout } from "../../../api/hooks/user";
import styles from "./HamburgerMenu.module.css";
import PerfilIcon from "../../Icon/PerfilIcon";

/**
 * HamburgerMenu — Bundle 5c.A
 *
 * Top-right slide-over menu for mobile/tablet (desktop unchanged).
 * - Slides in from right edge, ~75% viewport width
 * - Dimmed backdrop, tap outside or X closes
 * - ESC key closes (a11y)
 * - Focus trap within panel while open (a11y)
 * - Body scroll locked while open
 *
 * Logged-out content: Entrar, Criar conta, legal pages
 * Logged-in content: avatar + name, activity links, legal pages, Sair
 */

interface HamburgerMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HamburgerMenuTrigger: React.FC<{ onClick: () => void }> = ({
  onClick,
}) => (
  <button
    className={styles.hamburgerBtn}
    onClick={onClick}
    aria-label="Abrir menu"
    aria-expanded={false}
    type="button"
  >
    <span className={styles.bar} />
    <span className={styles.bar} />
    <span className={styles.bar} />
  </button>
);

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ isOpen, onClose }) => {
  const { userId, user, setIsLoginOpen, setIsRegistrationOpen } =
    useContext(Contexts);
  const { logout } = useLogout();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLButtonElement>(null);

  const loggedIn = !!(userId && userId !== 0 && !user?.isGuest);
  // /operacao is master-only (same gate as the desktop footer); "Para parceiros"
  // is shown to everyone (logged-in and logged-out).
  const showOperacao = loggedIn && user?.is_affiliate_master === true;
  const displayName = user?.name || user?.nickName || user?.username || "";
  const userAvatar = user?.avatar || null;

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // ESC closes
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    // Focus first element
    const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (focusable.length > 0) focusable[0].focus();

    const trapFocus = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusableEls = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusableEls.length === 0) return;
      const first = focusableEls[0];
      const last = focusableEls[focusableEls.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => document.removeEventListener("keydown", trapFocus);
  }, [isOpen]);

  const go = useCallback(
    (path: string) => {
      onClose();
      navigate(path);
    },
    [onClose, navigate]
  );

  const handleLogout = useCallback(async () => {
    onClose();
    await logout();
    // navigate("/") removed — useLogout already navigates to "/" via finally clause
  }, [onClose, logout]);

  if (!isOpen) return null;

  const panel = (
    <>
      {/* Backdrop */}
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={styles.panel}
        ref={panelRef}
      >
        {/* Close button */}
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Fechar menu"
          ref={firstFocusRef}
          type="button"
        >
          ✕
        </button>

        {loggedIn ? (
          <>
            {/* Avatar + name */}
            <div className={styles.avatarRow}>
              {userAvatar ? (
                <img
                  src={userAvatar}
                  alt={displayName}
                  className={styles.avatarCircle}
                />
              ) : (
                <div className={styles.avatarFallback}>
                  <PerfilIcon size="24px" />
                </div>
              )}
              <div>
                <div className={styles.displayName}>{displayName}</div>
                <span
                  className={styles.editLink}
                  onClick={() => go("/perfil")}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && go("/perfil")}
                >
                  Editar perfil
                </span>
              </div>
            </div>

            <nav className={styles.navContent}>
              {/* ATIVIDADE */}
              <div className={styles.sectionLabel}>Atividade</div>
              <button className={styles.navLink} onClick={() => go("/perfil/indicacoes")}>
                Indicações
              </button>
              <button className={styles.navLink} onClick={() => go("/perfil/saques")}>
                Histórico de saques
              </button>
              <button className={styles.navLink} onClick={() => go("/my-tickets")}>
                Histórico de apostas
              </button>

              {/* PARCEIROS */}
              <div className={styles.sectionLabel}>Parceiros</div>
              <button className={styles.navLink} onClick={() => go("/parceiros")}>
                Para parceiros
              </button>
              {showOperacao && (
                <button className={styles.navLink} onClick={() => go("/operacao")}>
                  Painel do parceiro
                </button>
              )}

              {/* SOBRE */}
              <div className={styles.sectionLabel}>Sobre</div>
              <button className={styles.navLink} onClick={() => go("/about")}>
                Sobre nós
              </button>
              <button className={styles.navLink} onClick={() => go("/como-funciona")}>
                Como funciona
              </button>

              {/* SUPORTE */}
              <div className={styles.sectionLabel}>Suporte</div>
              <a
                className={styles.navLink}
                href="mailto:ajuda@levesorte.com.br"
                onClick={onClose}
              >
                Ajuda
              </a>
              <button className={styles.navLink} onClick={() => go("/jogo-responsavel")}>
                Jogo Responsável
              </button>

              {/* LEGAL */}
              <div className={styles.sectionLabel}>Legal</div>
              <button className={styles.navLink} onClick={() => go("/termos")}>
                Termos de Uso
              </button>
              <button className={styles.navLink} onClick={() => go("/privacidade")}>
                Política de Privacidade
              </button>

              {/* CONTA */}
              <div className={styles.sectionLabel}>Conta</div>
              <button
                className={`${styles.navLink} ${styles.navLinkDanger}`}
                onClick={handleLogout}
              >
                Sair
              </button>
            </nav>
          </>
        ) : (
          <>
            {/* Logged-out: just header spacing */}
            <div className={styles.loggedOutHeader} />

            <nav className={styles.navContent}>
              {/* CONTA */}
              <div className={styles.sectionLabel}>Conta</div>
              <button
                className={styles.navLink}
                onClick={() => {
                  onClose();
                  setIsLoginOpen(true);
                }}
              >
                Entrar
              </button>
              <button
                className={styles.navLink}
                onClick={() => {
                  onClose();
                  setIsRegistrationOpen(true);
                }}
              >
                Criar conta
              </button>

              {/* PARCEIROS */}
              <div className={styles.sectionLabel}>Parceiros</div>
              <button className={styles.navLink} onClick={() => go("/parceiros")}>
                Para parceiros
              </button>

              {/* SOBRE */}
              <div className={styles.sectionLabel}>Sobre</div>
              <button className={styles.navLink} onClick={() => go("/about")}>
                Sobre nós
              </button>
              <button className={styles.navLink} onClick={() => go("/como-funciona")}>
                Como funciona
              </button>

              {/* SUPORTE */}
              <div className={styles.sectionLabel}>Suporte</div>
              <a
                className={styles.navLink}
                href="mailto:ajuda@levesorte.com.br"
                onClick={onClose}
              >
                Ajuda
              </a>
              <button className={styles.navLink} onClick={() => go("/jogo-responsavel")}>
                Jogo Responsável
              </button>

              {/* LEGAL */}
              <div className={styles.sectionLabel}>Legal</div>
              <button className={styles.navLink} onClick={() => go("/termos")}>
                Termos de Uso
              </button>
              <button className={styles.navLink} onClick={() => go("/privacidade")}>
                Política de Privacidade
              </button>
            </nav>
          </>
        )}

        {/* Footer */}
        <div className={styles.panelFooter}>
          +18 · Jogue com responsabilidade
          <br />
          CNPJ 61.709.901/0001-41
        </div>
      </div>
    </>
  );

  return createPortal(panel, document.body);
};

export default HamburgerMenu;
